import { TriangleAlert } from 'lucide-react';
import {
  formatPermissionName,
  getDangerousPermissions,
  hasDangerousPermissions,
  isDangerousPermission,
} from '../../../../utils/androidUtils';
import { PortalCard, StatusPill } from '../../../portal';
import { DetailDisclosure } from '../releaseDetailParts';

interface PermissionsSectionProps {
  permissions: string[];
}

export function PermissionsSection({ permissions }: PermissionsSectionProps) {
  if (!permissions || permissions.length === 0) {
    return null;
  }

  const dangerousCount = getDangerousPermissions(permissions).length;
  const hasDangerous = hasDangerousPermissions(permissions);
  const sorted = [...permissions].sort((a, b) => {
    const aDangerous = isDangerousPermission(a);
    const bDangerous = isDangerousPermission(b);
    if (aDangerous !== bDangerous) return aDangerous ? -1 : 1;
    return a.localeCompare(b);
  });

  return (
    <PortalCard>
      <DetailDisclosure
        label="Permissions"
        count={permissions.length}
        trailing={
          hasDangerous ? (
            <StatusPill tone="review" icon={<TriangleAlert />}>
              {dangerousCount} sensitive
            </StatusPill>
          ) : undefined
        }
        className="mx-0 mb-0 mt-0 border-t-0"
        bodyClassName="border-t border-seeker-800 pt-6"
      >
        {hasDangerous && (
          <p className="mb-4 flex items-center gap-2 text-xs text-amber-400">
            <TriangleAlert className="size-4 shrink-0" aria-hidden="true" />
            Sensitive permissions appear first and require careful review.
          </p>
        )}
        <div className="grid grid-cols-1 gap-x-6 gap-y-1.5 sm:grid-cols-2">
          {sorted.map((permission) => {
            const dangerous = isDangerousPermission(permission);
            return (
              <div
                key={permission}
                className="flex min-w-0 items-baseline gap-2.5 py-1"
              >
                <span
                  aria-hidden="true"
                  className={`mt-1.5 size-1 shrink-0 rounded-full ${
                    dangerous ? 'bg-amber-400' : 'bg-seeker-500'
                  }`}
                />
                <code
                  className={`break-all font-mono text-xs ${
                    dangerous ? 'text-amber-300' : 'text-seeker-400'
                  }`}
                  title={permission}
                >
                  {formatPermissionName(permission)}
                </code>
              </div>
            );
          })}
        </div>
      </DetailDisclosure>
    </PortalCard>
  );
}
